
import { useState, useEffect, Suspense } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import {
  CheckCircle,
  CreditCard,
  Shield,
  Clock,
  Users,
  Globe,
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { apiClient } from '@/lib/api-client';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import { PaymentForm } from '@/components/register/payment-form';
import { LanguageSwitcher } from '@/components/language-switcher';
import { TenantLogo } from '@/components/shared/tenant-logo';
import { useBranding } from '@/contexts/branding-context';

const stripePublishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
const stripePromise =
  stripePublishableKey && stripePublishableKey.startsWith('pk')
    ? loadStripe(stripePublishableKey)
    : null;

// App download URLs
const playStoreUrl =
  'https://play.google.com/store/apps/details?id=com.flexgymmobile';
const appStoreUrl = 'https://apps.apple.com/us/app/flexgym-app/id6746732181';

// Apple App Store SVG Logo
const AppleLogo = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
  </svg>
);

// Google Play Store SVG Logo
const GooglePlayLogo = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path
      d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.61 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"
      fill="#34D399"
    />
  </svg>
);

interface Plan {
  _id: string;
  name: string;
  price: number;
  duration: number;
  durationType: string;
  daysPerMonth: number;
  status: string;
  currency: string;
  gyms: string[];
  description?: string;
  features?: string[];
}

interface Gym {
  _id: string;
  name: string;
  address: string;
  status: string;
}

function RegisterPageContent() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { businessName } = useBranding();

  // Set Albanian as default for registration page
  useEffect(() => {
    if (!localStorage.getItem('language')) {
      i18n.changeLanguage('sq');
      localStorage.setItem('language', 'sq');
    }
  }, [i18n]);

  const registerSchema = z
    .object({
      email: z.string().email(t('auth.invalidEmail')),
      phone: z.string().min(1, t('auth.fieldRequired')),
      password: z.string().min(6, t('auth.passwordTooShort')),
      confirmPassword: z.string(),
      name: z.string().min(1, t('auth.fieldRequired')),
      surname: z.string().min(1, t('auth.fieldRequired')),
      planId: z.string().min(1, t('auth.fieldRequired')),
      gymId: z.string().min(1, t('auth.fieldRequired')),
    })
    .refine((data) => data.password === data.confirmPassword, {
      message: t('auth.passwordsDoNotMatch'),
      path: ['confirmPassword'],
    });

  type RegisterForm = z.infer<typeof registerSchema>;
  const [step, setStep] = useState(1);
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);
  const [selectedGym, setSelectedGym] = useState<Gym | null>(null);
  const [selectedGymId, setSelectedGymId] = useState<string>('');
  const [selectedPlanId, setSelectedPlanId] = useState<string>('');
  const [plans, setPlans] = useState<Plan[]>([]);
  const [filteredPlans, setFilteredPlans] = useState<Plan[]>([]);
  const [gyms, setGyms] = useState<Gym[]>([]);
  const [formData, setFormData] = useState<RegisterForm | null>(null);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    watch,
    setValue,
    trigger,
  } = useForm<RegisterForm>({
    resolver: zodResolver(registerSchema),
    mode: 'onChange',
  });

  useEffect(() => {
    fetchPlansAndGyms();
  }, []);

  // Handle URL parameters for pre-selecting gym
  useEffect(() => {
    const gymParam = searchParams.get('gym');

    if (gymParam && gyms.length > 0 && plans.length > 0) {
      // Check if the gym exists in our gyms list
      const gymExists = gyms.find((gym) => gym._id === gymParam);

      if (gymExists) {
        // Pre-select the gym
        handleGymSelection(gymParam);
      }
    }
  }, [searchParams, gyms, plans]); // Depend on gyms and plans being loaded

  const fetchPlansAndGyms = async () => {
    try {
      const [plansResponse, gymsResponse] = await Promise.all([
        apiClient.get('/plans'),
        apiClient.get('/gyms'),
      ]);

      const allPlans = plansResponse.data || [];
      const allGyms = gymsResponse.data || [];

      // Filter out free plans (price = 0)
      const paidPlans = allPlans.filter((plan: Plan) => plan.price > 0);
      // Filter out inactive gyms (only show active gyms)
      // @ts-ignore
      const activeGyms = allGyms.filter((gym: Gym) => gym.active);

      setPlans(paidPlans);
      setGyms(activeGyms);
    } catch (error) {
      console.error('Error fetching plans and gyms:', error);
      toast({
        title: 'Error',
        description:
          'Failed to load plans and gyms. Make sure your backend is running.',
        variant: 'destructive',
      });
    }
  };

  // Handle gym selection and filter plans
  const handleGymSelection = (gymId: string) => {
    setSelectedGymId(gymId);
    setValue('gymId', gymId, { shouldValidate: true });
    trigger('gymId');

    // Find the selected gym
    const gym = gyms.find((g) => g._id === gymId);
    setSelectedGym(gym || null);

    // Filter plans that include this gym in their gyms array
    const plansForGym = plans.filter((plan) => plan.gyms.includes(gymId));
    setFilteredPlans(plansForGym);

    // Clear selected plan if it's not available for this gym
    const currentPlanId = watch('planId');
    if (currentPlanId && !plansForGym.find((p) => p._id === currentPlanId)) {
      setValue('planId', '', { shouldValidate: true });
      setSelectedPlan(null);
      setSelectedPlanId('');
    }
  };

  // Handle plan selection
  const handlePlanSelection = (planId: string) => {
    setSelectedPlanId(planId);
    setValue('planId', planId, { shouldValidate: true });
    trigger('planId');

    // Find the selected plan
    const plan = filteredPlans.find((p) => p._id === planId);
    setSelectedPlan(plan || null);
  };

  const onSubmit = async (data: RegisterForm) => {
    if (step === 1) {
      // Step 1: Just validate form and move to payment step (no payment intent yet!)
      setFormData(data);
      setStep(2); // Move to payment step where user enters card details
    }
  };

  // Handle successful payment and registration completion
  const handlePaymentSuccess = async (
    paymentIntentId: string | null,
    clientSecret: string
  ) => {
    // PaymentForm has already completed the entire flow
    // Just move to success step
    setStep(3);
  };

  const formatPrice = (price: number, currency: string = 'EUR') => {
    // Divide price by 100 to convert from cents to euros
    const adjustedPrice = price / 100;

    if (currency === 'ALL') {
      return `${adjustedPrice.toLocaleString()} ALL`;
    }
    if (currency === 'EUR') {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'EUR',
      }).format(adjustedPrice);
    }
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    }).format(adjustedPrice);
  };

  const handlePlayStore = () => {
    window.open(playStoreUrl, '_blank');
  };

  const handleAppStore = () => {
    window.open(appStoreUrl, '_blank');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/10 to-primary/5 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Language Switcher */}
        <div className="flex justify-end mb-4">
          <div className="w-auto">
            <LanguageSwitcher />
          </div>
        </div>

        {/* Header */}
        <div className="text-center mb-8 sm:mb-12">
          <div className="flex justify-center mb-6">
            <TenantLogo className="h-14 w-auto" />
          </div>
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-foreground mb-4">
            {t('auth.joinGym', { name: businessName })}
          </h1>
          <p className="text-lg sm:text-xl text-muted-foreground max-w-2xl mx-auto px-4">
            {t('auth.fitnessJourney')}
          </p>
        </div>

        {/* Progress Indicator */}
        <div className="max-w-2xl mx-auto mb-8">
          <div className="flex items-center justify-center space-x-4 sm:space-x-8 max-w-md mx-auto">
            <div
              className={`flex items-center ${
                step >= 1 ? 'text-primary' : 'text-gray-400'
              }`}
            >
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center border-2 ${
                  step >= 1
                    ? 'border-primary bg-primary text-primary-foreground'
                    : 'border-gray-300'
                }`}
              >
                {step > 1 ? <CheckCircle className="w-5 h-5" /> : '1'}
              </div>
              <span className="ml-2 font-medium text-sm sm:text-base hidden md:inline">
                {t('auth.registrationStep')}
              </span>
            </div>
            <div
              className={`w-12 sm:w-24 h-0.5 ${
                step >= 2 ? 'bg-primary' : 'bg-gray-300'
              }`}
            ></div>
            <div
              className={`flex items-center ${
                step >= 2 ? 'text-primary' : 'text-gray-400'
              }`}
            >
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center border-2 ${
                  step >= 2
                    ? 'border-primary bg-primary text-primary-foreground'
                    : 'border-gray-300'
                }`}
              >
                {step > 2 ? <CheckCircle className="w-5 h-5" /> : '2'}
              </div>
              <span className="ml-2 font-medium text-sm sm:text-base hidden md:inline">
                {t('auth.paymentStep')}
              </span>
            </div>
            <div
              className={`w-12 sm:w-24 h-0.5 ${
                step >= 3 ? 'bg-primary' : 'bg-gray-300'
              }`}
            ></div>
            <div
              className={`flex items-center ${
                step >= 3 ? 'text-primary' : 'text-gray-400'
              }`}
            >
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center border-2 ${
                  step >= 3
                    ? 'border-primary bg-primary text-primary-foreground'
                    : 'border-gray-300'
                }`}
              >
                {step >= 3 ? <CheckCircle className="w-5 h-5" /> : '3'}
              </div>
              <span className="ml-2 font-medium text-sm sm:text-base hidden md:inline">
                {t('auth.completeStep')}
              </span>
            </div>
          </div>
        </div>

        {/* Step 1: Registration Form */}
        {step === 1 && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {/* Form */}
            <div className={`${selectedPlan ? 'lg:col-span-2' : 'lg:col-span-3'}`}>
              <Card>
                <CardHeader>
                  <CardTitle>{t('auth.personalInfo')}</CardTitle>
                  <CardDescription>
                    {t('auth.selectGymAndPlan')}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                    {/* Gym Selection - First */}
                    <div>
                      <Label htmlFor="gymId">{t('auth.selectGym')}</Label>
                      <Select
                        value={selectedGymId}
                        onValueChange={handleGymSelection}
                      >
                        <SelectTrigger
                          className={errors.gymId ? 'border-red-500' : ''}
                        >
                          <SelectValue placeholder={t('auth.selectGym')} />
                        </SelectTrigger>
                        <SelectContent>
                          {gyms.map((gym) => (
                            <SelectItem key={gym._id} value={gym._id}>
                              {gym.name} - {gym.address}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {errors.gymId && (
                        <p className="text-sm text-red-500 mt-1">
                          {errors.gymId.message}
                        </p>
                      )}
                    </div>

                    {/* Plan Selection - Only show when gym is selected */}
                    {selectedGymId ? (
                      <div>
                        <Label htmlFor="planId">{t('auth.selectPlan')}</Label>
                        <Select
                          value={selectedPlanId}
                          onValueChange={handlePlanSelection}
                        >
                          <SelectTrigger
                            className={errors.planId ? 'border-red-500' : ''}
                          >
                            <SelectValue placeholder={t('auth.selectPlan')} />
                          </SelectTrigger>
                          <SelectContent>
                            {filteredPlans.length > 0 ? (
                              filteredPlans.map((plan) => (
                                <SelectItem key={plan._id} value={plan._id}>
                                  {plan.name} - {formatPrice(plan.price, 'EUR')}{' '}
                                  / {plan.duration} {plan.durationType}
                                </SelectItem>
                              ))
                            ) : (
                              <SelectItem value="" disabled>
                                {t('auth.noPlansAvailable')}
                              </SelectItem>
                            )}
                          </SelectContent>
                        </Select>
                        {errors.planId && (
                          <p className="text-sm text-red-500 mt-1">
                            {errors.planId.message}
                          </p>
                        )}
                      </div>
                    ) : (
                      <div className="bg-primary/10 p-3 rounded-lg">
                        <p className="text-sm text-primary">
                          {t('auth.selectGymFirst')}
                        </p>
                      </div>
                    )}

                    <Separator />

                    {/* Personal Information */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="name">{t('auth.firstName')}</Label>
                        <Input
                          id="name"
                          {...register('name')}
                          className={errors.name ? 'border-red-500' : ''}
                        />
                        {errors.name && (
                          <p className="text-sm text-red-500 mt-1">
                            {errors.name.message}
                          </p>
                        )}
                      </div>
                      <div>
                        <Label htmlFor="surname">{t('auth.lastName')}</Label>
                        <Input
                          id="surname"
                          {...register('surname')}
                          className={errors.surname ? 'border-red-500' : ''}
                        />
                        {errors.surname && (
                          <p className="text-sm text-red-500 mt-1">
                            {errors.surname.message}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Contact Information */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="email">{t('auth.email')}</Label>
                        <Input
                          id="email"
                          type="email"
                          {...register('email')}
                          className={errors.email ? 'border-red-500' : ''}
                        />
                        {errors.email && (
                          <p className="text-sm text-red-500 mt-1">
                            {errors.email.message}
                          </p>
                        )}
                      </div>
                      <div>
                        <Label htmlFor="phone">{t('auth.phone')}</Label>
                        <Input
                          id="phone"
                          {...register('phone')}
                          className={errors.phone ? 'border-red-500' : ''}
                        />
                        {errors.phone && (
                          <p className="text-sm text-red-500 mt-1">
                            {errors.phone.message}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Password */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="password">{t('auth.password')}</Label>
                        <Input
                          id="password"
                          type="password"
                          {...register('password')}
                          className={errors.password ? 'border-red-500' : ''}
                        />
                        {errors.password && (
                          <p className="text-sm text-red-500 mt-1">
                            {errors.password.message}
                          </p>
                        )}
                      </div>
                      <div>
                        <Label htmlFor="confirmPassword">
                          {t('auth.confirmPassword')}
                        </Label>
                        <Input
                          id="confirmPassword"
                          type="password"
                          {...register('confirmPassword')}
                          className={
                            errors.confirmPassword ? 'border-red-500' : ''
                          }
                        />
                        {errors.confirmPassword && (
                          <p className="text-sm text-red-500 mt-1">
                            {errors.confirmPassword.message}
                          </p>
                        )}
                      </div>
                    </div>

                    <Button
                      type="submit"
                      className="w-full"
                      disabled={!isValid || loading}
                    >
                      {loading
                        ? t('auth.settingUpPayment')
                        : t('auth.continueToPayment')}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>

            {/* Plan Preview */}
            {selectedPlan && (
              <div className="lg:col-span-1">
                <Card className="sticky top-4">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <CreditCard className="w-5 h-5" />
                      {selectedPlan.name}
                    </CardTitle>
                    <CardDescription>{t('auth.selectedPlan')}</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="text-center">
                      <div className="text-3xl font-bold text-primary">
                        {formatPrice(selectedPlan.price, 'EUR')}
                      </div>
                      <div className="text-gray-600">
                        {t('auth.per')} {selectedPlan.duration}{' '}
                        {selectedPlan.durationType}
                      </div>
                    </div>

                    <Separator />

                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-green-600" />
                        <span className="text-sm">
                          {selectedPlan.daysPerMonth} {t('auth.daysPerMonth')}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Shield className="w-4 h-4 text-green-600" />
                        <span className="text-sm">
                          {t('auth.fullGymAccess')}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Users className="w-4 h-4 text-green-600" />
                        <span className="text-sm">
                          {t('auth.groupClassesIncluded')}
                        </span>
                      </div>
                    </div>

                    {selectedPlan.features &&
                      selectedPlan.features.length > 0 && (
                        <>
                          <Separator />
                          <div className="space-y-2">
                            <h4 className="font-semibold text-sm">
                              {t('auth.planFeatures')}:
                            </h4>
                            {selectedPlan.features.map((feature, index) => (
                              <div
                                key={index}
                                className="flex items-center gap-2"
                              >
                                <CheckCircle className="w-4 h-4 text-green-600" />
                                <span className="text-sm">{feature}</span>
                              </div>
                            ))}
                          </div>
                        </>
                      )}
                  </CardContent>
                </Card>
              </div>
            )}
          </div>
        )}

        {/* Step 2: Payment */}
        {step === 2 && selectedPlan && formData && (
          <div className="max-w-2xl mx-auto">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CreditCard className="w-6 h-6" />
                  {t('auth.payment.completePayment')}
                </CardTitle>
                <CardDescription>
                  {t('auth.payment.paymentDescription')}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {/* Payment Summary */}
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="font-semibold text-gray-900 mb-2">
                      {t('auth.payment.paymentSummary')}
                    </h3>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">{selectedPlan.name}</span>
                      <span className="font-semibold">
                        {formatPrice(selectedPlan.price, selectedPlan.currency)}
                      </span>
                    </div>
                  </div>

                  {/* Enhanced Payment Form - Creates payment intent when card is entered */}
                  {stripePromise ? (
                    <Elements stripe={stripePromise}>
                      <PaymentForm
                        amount={selectedPlan.price / 100}
                        currency="EUR"
                        planName={selectedPlan.name}
                        customerData={{
                          email: formData.email,
                          name: formData.name,
                          surname: formData.surname,
                          phone: formData.phone,
                          planId: formData.planId,
                          gymId: formData.gymId,
                          password: formData.password,
                        }}
                        onSuccess={handlePaymentSuccess}
                      />
                    </Elements>
                  ) : (
                    <p className="text-sm text-muted-foreground py-4">
                      {t('auth.payment.notConfigured', 'Payment is not configured. Please set REACT_APP_STRIPE_PUBLISHABLE_KEY.')}
                    </p>
                  )}

                  <div className="text-center">
                    <Button
                      variant="outline"
                      onClick={() => setStep(1)}
                      disabled={loading}
                      className="w-full"
                    >
                      {t('auth.backToRegistration')}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Step 3: Success */}
        {step === 3 && (
          <div className="max-w-2xl mx-auto text-center">
            <Card>
              <CardContent className="pt-6">
                <div className="space-y-6">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                    <CheckCircle className="w-8 h-8 text-green-600" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-foreground mb-2">
                      {t('auth.registrationComplete')}
                    </h2>
                    <p className="text-muted-foreground">
                      {t('auth.welcomeToGym')}
                    </p>
                  </div>
                  <div className="bg-primary/10 p-4 rounded-lg space-y-3">
                    <p className="text-sm text-primary">
                      <strong>{t('auth.nextSteps')}</strong>
                    </p>
                    <p className="text-sm text-primary">
                      {t('auth.downloadAppMessage')}
                    </p>
                    <p className="text-sm text-primary">
                      {t('auth.visitGymMessage')}
                    </p>
                  </div>

                  {/* App Download Buttons */}
                  <div className="space-y-3">
                    <p className="text-sm font-medium text-foreground text-center">
                      {t('auth.downloadApp')}
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <Button
                        onClick={handleAppStore}
                        className="w-full h-16 rounded-3xl bg-black border-2 border-red-500 text-white hover:bg-red-500/10 flex items-center justify-between px-6 text-left transition-all duration-200 hover:scale-105"
                        variant="ghost"
                      >
                        <div className="flex items-center space-x-4">
                          <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center text-black">
                            <AppleLogo />
                          </div>
                          <div className="text-left">
                            <div className="font-medium text-sm text-white">
                              Download on the App Store
                            </div>
                            <div className="text-xs text-gray-300">
                              Download for iPhone
                            </div>
                          </div>
                        </div>
                        <div className="text-red-500">
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </div>
                      </Button>

                      <Button
                        onClick={handlePlayStore}
                        className="w-full h-16 rounded-3xl bg-black border-2 border-red-500 text-white hover:bg-red-500/10 flex items-center justify-between px-6 text-left transition-all duration-200 hover:scale-105"
                        variant="ghost"
                      >
                        <div className="flex items-center space-x-4">
                          <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center">
                            <GooglePlayLogo />
                          </div>
                          <div className="text-left">
                            <div className="font-medium text-sm text-white">
                              Get it on Google Play
                            </div>
                            <div className="text-xs text-gray-300">
                              Download for Android
                            </div>
                          </div>
                        </div>
                        <div className="text-red-500">
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </div>
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Already have an account link */}
        {step === 1 && (
          <div className="text-center mt-8">
            <p className="text-gray-600">
              {t('auth.alreadyHaveAccount')}{' '}
              <Button
                variant="link"
                onClick={() => navigate('/login')}
                className="p-0 h-auto"
              >
                {t('auth.signIn')}
              </Button>
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

// Loading component for Suspense fallback
function RegisterPageLoading() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/10 to-primary/5 py-12 px-4 sm:px-6 lg:px-8 flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary mx-auto"></div>
        <p className="mt-4 text-lg text-gray-600">
          Loading registration form...
        </p>
      </div>
    </div>
  );
}

// Main export with Suspense boundary
export default function RegisterPage() {
  return (
    <Suspense fallback={<RegisterPageLoading />}>
      <RegisterPageContent />
    </Suspense>
  );
}
